import dayjs from 'dayjs';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { type FC, Fragment } from 'react';

// MUI
import { yellow } from '@mui/material/colors';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

// screen
import type { BalanceCostModel, Day } from 'modules/map/components/balance-cost/types';
import { formatUTC } from 'modules/map/components/pdf-print-form/utils';
import { checkIfNumber, getMaxRowCounts } from 'modules/map/components/balance-cost/utils';

const RowInDay = ({
  day,
  dayIndex,
  rowsCount,
  showAfternoon,
}: {
  day: Day;
  dayIndex: number;
  rowsCount: number;
  showAfternoon: boolean;
}) => {
  const item = day.items[dayIndex];
  const additionCharge = day.additionCharges[dayIndex];

  const noteFormatting = () => {
    const dayNote = day.note || '';
    const shouldBreakNotes = dayNote.includes('\n');

    return shouldBreakNotes
      ? dayNote.split('\n').map((note, index) => {
          return (
            <Typography key={index} sx={{ my: 1 }}>
              {note}
            </Typography>
          );
        })
      : dayNote;
  };

  const row = {
    isFistRow: dayIndex === 0,
    dish: day.dishes[dayIndex],
    name: item?.name || '',
    quantity: checkIfNumber(item?.quantity) ? item.quantity : '',
    unit: item?.unit || '',
    price: checkIfNumber(item?.price) ? item.price : '',
    raw: item?.raw || '',
    additionChargeName: additionCharge?.name || '',
    additionChargePrice: checkIfNumber(additionCharge?.price) ? additionCharge.price : '',
    afternoon: day.afternoons[dayIndex] || [],
    note: noteFormatting(),
  };

  return (
    <TableRow key={dayIndex}>
      {/* THỨ/NGÀY */}
      {row.isFistRow && (
        <TableCell rowSpan={rowsCount}>
          <span className="capitalize">{formatUTC(day.datetime, 'dddd')}</span>
          <br />
          {formatUTC(day.datetime, 'L')}
        </TableCell>
      )}

      {/* THỰC ĐƠN */}
      <TableCell>{row.dish}</TableCell>

      {/* CHI TIẾT */}
      <TableCell>{row.name}</TableCell>

      {/* SL(g) */}
      <TableCell>
        {checkIfNumber(row.quantity) && (
          <FormattedNumber
            value={row.quantity as number}
            minimumFractionDigits={1}
            maximumFractionDigits={1}
          />
        )}
      </TableCell>

      {/* ĐG/Kg */}
      <TableCell>{row.unit}</TableCell>

      {/* TT */}
      <TableCell>
        {checkIfNumber(row.price) && <FormattedNumber value={row.price as number} />}
      </TableCell>

      {/* TP Chín */}
      <TableCell>{row.raw}</TableCell>

      {/* Chi phí phụ */}
      <TableCell>{row.additionChargeName}</TableCell>

      {/* Số tiền */}
      <TableCell>
        {checkIfNumber(row.additionChargePrice) && (
          <FormattedNumber value={row.additionChargePrice as number} />
        )}
      </TableCell>

      {/* GHI CHÚ / QUÀ CHIỀU */}
      {showAfternoon ? (
        <TableCell>
          {row.afternoon.map((item, index) => (
            <Box key={index} sx={{ my: 1 }}>
              {item}
            </Box>
          ))}
        </TableCell>
      ) : (
        row.isFistRow && <TableCell rowSpan={rowsCount}>{row.note}</TableCell>
      )}

      {/* Thuế GTGT */}
      <TableCell />

      {/* Tổng tiền */}
      <TableCell />
    </TableRow>
  );
};

const TotalInDay = ({
  prices,
  showAfternoon,
}: {
  prices: Day['price'];
  showAfternoon: boolean;
}) => {
  return (
    <TableRow
      sx={(theme) => ({
        bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : yellow[200],
        '& td': { fontWeight: 700 },
      })}
    >
      {/* THỨ/NGÀY */}
      <TableCell />

      {/* THỰC ĐƠN */}
      <TableCell />

      {/* CHI TIẾT */}
      <TableCell>
        <FormattedMessage id="map.totalQuantity" />
      </TableCell>

      {/* SL(g) */}
      <TableCell />

      {/* ĐG/Kg */}
      <TableCell />

      {/* TT */}
      <TableCell>{prices?.itemPrice && <FormattedNumber value={prices.itemPrice} />}</TableCell>

      {/* TP Chín */}
      <TableCell />

      {/* Chi phí phụ */}
      <TableCell />

      {/* Số tiền */}
      <TableCell>
        {prices?.additionPrice && <FormattedNumber value={prices.additionPrice} />}
      </TableCell>

      {/* GHI CHÚ / QUÀ CHIỀU */}
      <TableCell>
        {showAfternoon && prices?.afternoon && <FormattedNumber value={prices.afternoon} />}
      </TableCell>

      {/* Thuế GTGT */}
      <TableCell>{prices?.tax && <FormattedNumber value={prices.tax} />}</TableCell>

      {/* Tổng tiền */}
      <TableCell>{prices?.total && <FormattedNumber value={prices.total} />}</TableCell>
    </TableRow>
  );
};

interface Props {
  data: BalanceCostModel;
}

const BalanceTable: FC<Props> = ({ data }) => {
  const days = data?.structuredData?.data?.items || [];
  const fromDate = formatUTC(days[0]?.datetime || '', 'L');

  const lastItemIndex = days.length - 1;
  const toDate = formatUTC(days[lastItemIndex]?.datetime || '', 'L');
  const showAfternoon = data.structuredData.metadata.hasAfternoon;

  return (
    <TableContainer
      className="px-2 tablet:px-4"
      sx={{
        mt: 2,
        '& td,th': {
          border: '0.5px solid',
          textAlign: 'center',
          padding: 1,
        },
        '@media print': {
          '& table': {
            borderCollapse: 'collapse',
          },
          '& td,th': {
            background: 'none',
            color: 'black',
          },
          '& thead': {
            background: 'none',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'none',
          '@media print': {
            display: 'flex',
            justifyContent: 'space-round',
          },
        }}
      >
        <Typography variant="body1">{dayjs().format('LL')}</Typography>
        <Typography variant="h6" textAlign={'center'} flex={1}>
          <FormattedMessage id="map.balanceMenu" />
          &nbsp;({[fromDate, toDate].join(' - ')})
        </Typography>
        <Typography variant="h6">
          <FormattedMessage id="map.process" />
          :&nbsp;
          <FormattedMessage id="map.completed" />
        </Typography>
      </Box>
      <Table
        sx={(theme) => ({
          borderCollapse: 'separate',
          '& th, & td': { borderRight: `1px solid ${theme.palette.divider}` },
        })}
      >
        <TableHead
          sx={(theme) => ({
            position: 'sticky',
            top: 0,
            bgcolor: theme.palette.background.paper,
          })}
        >
          <TableRow>
            {[
              {
                rowSpan: 2,
                label: <FormattedMessage id="map.day" />,
              },
              {
                rowSpan: 2,
                label: <FormattedMessage id="map.menu" />,
              },
              {
                rowSpan: 2,
                label: <FormattedMessage id="map.detail" />,
              },
              {
                rowSpan: 2,
                label: <FormattedMessage id="map.sl" />,
              },
              {
                rowSpan: 2,
                label: <FormattedMessage id="map.unit" />,
              },
              {
                rowSpan: 2,
                label: <FormattedMessage id="map.tt" />,
              },
              {
                rowSpan: 2,
                label: <FormattedMessage id="map.cooked" />,
              },
              {
                colSpan: 2,
                label: <FormattedMessage id="map.extraFee" />,
              },
              {
                rowSpan: 2,
                label: <FormattedMessage id={showAfternoon ? 'map.afternoon' : 'map.note'} />,
              },
              {
                rowSpan: 2,
                label: <FormattedMessage id="map.vat" />,
              },
              {
                rowSpan: 2,
                label: <FormattedMessage id="map.totalPrice" />,
                width: '10%',
              },
            ].map((item, index) => {
              return (
                <TableCell
                  key={index}
                  className="whitespace-pre-wrap"
                  colSpan={item?.colSpan}
                  rowSpan={item?.rowSpan}
                  style={{ width: item?.width }}
                >
                  {item?.label}
                </TableCell>
              );
            })}
          </TableRow>
          <TableRow>
            {['map.extraFee', 'map.price'].map((intlLabel, index) => {
              return (
                <TableCell key={index} className="whitespace-pre-wrap">
                  <FormattedMessage id={intlLabel} />
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {days.map((day, idx) => {
            const rowsCount = getMaxRowCounts({
              ingredients: day.items.length,
              dishes: day.dishes.length,
              additionCharges: day.additionCharges.length,
              afternoons: day.afternoons.length,
            });

            return (
              <Fragment key={idx}>
                {Array(rowsCount)
                  .fill(0)
                  .map((_, index) => (
                    <RowInDay
                      key={index}
                      day={day}
                      dayIndex={index}
                      rowsCount={rowsCount}
                      showAfternoon={showAfternoon}
                    />
                  ))}
                <TotalInDay prices={day.price} showAfternoon={showAfternoon} />
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
export default BalanceTable;
